import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Store, Loader2, Info, X } from 'lucide-react';
import { useProgramContext } from '../../context/ProgramContext';
import SelectContextFirst from '../../components/SelectContextFirst';
import type { Tenant } from '../../types/domain';

type ShopFilter = 'all' | 'withVpa' | 'withoutVpa';

function ShopsScreen() {
  const navigate = useNavigate();
  const { tenants, loading, selectedProgramId, isWalletAddressCollection } = useProgramContext();
  const [search, setSearch] = useState('');
  const [shopFilter, setShopFilter] = useState<ShopFilter>('all');

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return tenants.filter(
      (t: Tenant | (Tenant & { storeCoordinator?: string; phone?: string | number })) => {
        const matchQuery =
          !q ||
          (t.tenantName || '').toLowerCase().includes(q) ||
          (t.tenantBrand || '').toLowerCase().includes(q) ||
          ((t as any).storeCoordinator || '').toLowerCase().includes(q) ||
          ((t as any).phone || '').toString().includes(q);

        const count = t.vpaCount ?? 0;
        const matchFilter =
          shopFilter === 'all' ||
          (shopFilter === 'withVpa' && count > 0) ||
          (shopFilter === 'withoutVpa' && count === 0);

        return matchQuery && matchFilter;
      },
    );
  }, [tenants, search, shopFilter]);

  return (
    <div className="min-h-full bg-surface">
      <header className="fc-header">
        <h1 className="flex items-center gap-2 text-sm sm:text-base font-semibold text-slate-900">
          <Store className="w-5 h-5 text-brand" />
          Shops
        </h1>
      </header>

      <div className="p-3 pb-5 space-y-4">
        {selectedProgramId == null ? (
          <SelectContextFirst />
        ) : (
          <>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search shop..."
                className="fc-input pl-11"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <p className="text-xs text-slate-500 px-1">
              {filtered.length} shop{filtered.length !== 1 ? 's' : ''}
            </p>
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {[
                { key: 'all', label: 'All' },
                {
                  key: 'withVpa',
                  label: isWalletAddressCollection ? 'With wallet' : 'With VPA',
                },
                {
                  key: 'withoutVpa',
                  label: isWalletAddressCollection ? 'No wallet' : 'No VPA',
                },
              ].map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setShopFilter(f.key as ShopFilter)}
                  className={`h-8 px-3 rounded-full text-xs font-semibold border whitespace-nowrap ${
                    shopFilter === f.key
                      ? 'bg-brand/10 text-brand border-brand/30'
                      : 'bg-white text-slate-600 border-slate-200'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {loading && tenants.length === 0 ? (
              <div className="fc-card-muted p-8 text-center text-gray-500 flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-brand" />
                <span>Loading shops...</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="bg-white border border-dashed border-slate-200 rounded-lg p-8 text-center text-slate-500 flex flex-col items-center gap-3 transition-colors">
                <Store className="w-10 h-10 text-slate-300" />
                <span>{search ? 'No shops match your search' : 'No shops in this program'}</span>
              </div>
            ) : (
              <div className="space-y-2">
                {filtered.map((shop) => (
                  <div
                    key={shop.tenantId}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/app/shops/${shop.tenantId}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/app/shops/${shop.tenantId}`);
                      }
                    }}
                    className="fc-list-item"
                  >
                    <div className="flex items-center gap-3">
                      {shop.storeLogo && (
                        <img
                          src={shop.storeLogo}
                          alt=""
                          className="w-10 h-10 object-contain rounded border border-slate-100 bg-white"
                        />
                      )}
                      <div>
                        <p className="font-medium text-slate-900">{shop.tenantName}</p>
                        <p className="text-sm text-slate-500">{shop.tenantBrand}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-500">
                        {shop.vpaCount ?? 0}{' '}
                        {isWalletAddressCollection ? 'wallets' : 'VPAs'}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/app/shops/${shop.tenantId}`, { state: { openInfo: true } });
                        }}
                        className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-slate-100 active:bg-slate-200"
                        aria-label="View shop details"
                      >
                        <Info className="w-4 h-4 text-slate-500" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ShopsScreen;
